import { Pagination } from "@mui/material";
import { UploadIcon } from "@radix-ui/react-icons";
import { Loader2, MoreHorizontal } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { ThreeDots } from "react-loader-spinner";
import { Link } from "react-router-dom";
import { checkTagValidity } from "../../Utils/Utils";
import {
  ADD_PBO,
  CHARGER_LISTE_PBO,
  DELETE_PBO,
  EXPORTER_LISTE_BRIN,
  UPDATE_PBO,
} from "../../Utils/constant";
import model from "../../assets/model-fichier/model_fichier.xlsx";
import { ConnectIcon } from "../../components/Icons/Connect";
import { Loader } from "../../components/Widget/Loader";
import { deleteOne, store, updateOne } from "../../services/service";
import { usePBOStore } from "../../store/pboStore/listPboStore";

export const PointBranchementOptique = () => {
  const [selectItem, setSelectItem] = useState(null);
  const [fichier, setFichier] = useState([]);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();
  const [exportLoading, setExportLoading] = useState(false);
  const [isSubmit, setIsSubmit] = useState(false);
  const [error, setError] = useState("");
  const [loadingLocalisation, setLoadingLocalisation] = useState(false);
  const [longitude, setLongitude] = useState(null);
  const [latitude, setLatitude] = useState(null);
  const listPbo = usePBOStore();
  const [message, setMessage] = useState("");

  const handleChangeFichier = (e) => {
    const newFiles = Array.from(e.target.files);
    setFichier((prev) => [...prev, ...newFiles]);
  };

  const handleClick = () => {
    setLoadingLocalisation(true);
    if (!("geolocation" in navigator)) {
      setLoadingLocalisation(false);
      setError(
        "La géolocalisation n'est pas prise en charge par ce navigateur.",
      );
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLoadingLocalisation(false);
        setLatitude(position.coords.latitude);
        setLongitude(position.coords.longitude);
        setError(null);
      },
      (err) => {
        setLoadingLocalisation(false);
        setError(err.message);
      },
      { timeout: 10000, maximumAge: 0, enableHighAccuracy: true },
    );
  };

  const submit = (data) => {
    setIsSubmit(true);
    const req = selectItem
      ? updateOne(UPDATE_PBO, selectItem.id, data)
      : store(ADD_PBO, "application/json", data);

    req
      .then((res) => {
        if (res.status === 200) {
          listPbo.getData("page=0&param=&size=30");
          document.getElementById("add_pbo").close();
          toast.success("Enregistrement effectué");
          setIsSubmit(false);
          reset();
          setLongitude(null);
          setLatitude(null);
        }
      })
      .catch((err) => {
        setIsSubmit(false);
        setError(err?.response?.data?.donnee);
      });
  };

  useEffect(() => {
    if (selectItem)
      reset({
        latitude: selectItem?.latitude,
        longitude: selectItem?.longitude,
      });
    else reset();
  }, [selectItem, reset]);

  useEffect(() => {
    listPbo.getData("page=0&param=&size=30");
  }, []);

  const more = (page) => listPbo.getData(`page=${page - 1}&param=&size=30`);

  const openAdd = () => {
    setSelectItem(null);
    setError("");
    reset({ latitude: "", longitude: "" });
    document.getElementById("add_pbo").showModal();
  };

  const inputCls =
    "w-full h-10 px-3 border border-gray-200 rounded-lg text-sm text-gray-900 bg-gray-50 outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-100 transition";
  const closeBtnCls =
    "w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-500 text-sm font-semibold transition";

  return (
    <div>
      {/* Header */}
      <div className="mt-4 mb-1">
        <p className="text-3xl font-mtn font-bold text-gray-900 tracking-tight">
          Points de branchement optique
        </p>
        <p className="text-sm text-gray-400 mt-1">
          Gestion des PBO et de leurs coupleurs
        </p>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 py-4 border-b border-gray-100 mb-6">
        <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 h-10">
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#9ca3af"
            strokeWidth="2"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
          <input
            onChange={(e) =>
              listPbo.getData(`page=0&param=${e.target.value}&size=30`)
            }
            type="text"
            placeholder="Rechercher un PBO…"
            className="bg-transparent border-none outline-none text-sm text-gray-900 placeholder-gray-400 w-48"
          />
        </div>

        <div className="ml-auto flex items-center gap-2">
          {checkTagValidity("AJOUTER_PBO") && (
            <button
              onClick={openAdd}
              className="h-10 px-4 bg-yellow-400 hover:bg-yellow-300 text-gray-900 text-sm font-semibold rounded-xl transition"
            >
              Ajouter un PBO
            </button>
          )}

          <button
            onClick={() =>
              document.getElementById("modal_eligible").showModal()
            }
            className="h-10 px-4 flex items-center gap-2 bg-gray-900 hover:bg-gray-700 text-white text-sm font-medium rounded-xl transition"
          >
            <UploadIcon />
            Charger un fichier
          </button>

          <button
            disabled={exportLoading}
            onClick={async () => {
              setExportLoading(true);
              try {
                const res = await store(
                  EXPORTER_LISTE_BRIN + `?statut=`,
                  null,
                  null,
                  { responseType: "blob" },
                );
                if (res?.data) {
                  const url = window.URL.createObjectURL(res.data);
                  const a = document.createElement("a");
                  a.href = url;
                  a.setAttribute("download", "liste_brin.xlsx");
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                }
              } catch {
                toast.error("Erreur lors de l'export");
              } finally {
                setExportLoading(false);
              }
            }}
            className={`h-10 px-4 flex items-center gap-2 text-sm font-medium rounded-xl transition ${
              exportLoading
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-200"
            }`}
          >
            {exportLoading ? (
              <>
                <Loader2 className="animate-spin w-4 h-4" /> Export…
              </>
            ) : (
              "Exporter les brins"
            )}
          </button>
        </div>
      </div>

      {/* Upload Modal */}
      <dialog id="modal_eligible" className="modal">
        <div className="modal-box rounded-2xl p-6 max-w-lg">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-bold text-gray-900">
              Chargement par fichier
            </h2>
            <form method="dialog">
              <button id="close_upload" className={closeBtnCls}>
                ✕
              </button>
            </form>
          </div>

          {message && <p className="text-xs text-red-500 mb-3">{message}</p>}

          <label className="block w-full cursor-pointer">
            <div className="border-2 border-dashed border-gray-200 bg-gray-50 rounded-xl p-8 flex flex-col items-center gap-3 hover:border-yellow-400 hover:bg-yellow-50 transition">
              <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center text-gray-400">
                <UploadIcon />
              </div>
              <p className="text-sm text-gray-500 font-medium">
                {fichier.length > 0 ? (
                  <span className="text-yellow-600 font-semibold">
                    {fichier[0]?.name}
                  </span>
                ) : (
                  "Glisser ou cliquer pour charger"
                )}
              </p>
              <p className="text-xs text-gray-400">.xlsx uniquement</p>
              <input
                type="file"
                id="fichier"
                onChange={handleChangeFichier}
                className="hidden"
              />
            </div>
          </label>

          <div className="flex items-center gap-2 mt-4">
            <a
              href={model}
              download="modelPBO"
              className="h-9 px-4 flex items-center gap-1.5 border border-gray-200 rounded-lg text-xs font-medium text-gray-700 hover:bg-gray-50 transition"
            >
              Télécharger le modèle
            </a>
            <button
              onClick={() => {
                setIsSubmit(true);
                store(CHARGER_LISTE_PBO, "multipart/form-data", {
                  liste: fichier[0],
                })
                  .then((res) => {
                    if (res.status === 200) {
                      setIsSubmit(null);
                      listPbo.getData("page=0&param=&size=8");
                      document.getElementById("close_upload").click();
                      toast.success("Fichier chargé avec succès");
                    }
                  })
                  .catch((err) => {
                    setIsSubmit(false);
                    setMessage(err?.response?.data?.donnee);
                  });
              }}
              disabled={isSubmit || fichier.length === 0}
              className={`ml-auto h-9 px-4 rounded-lg text-xs font-semibold flex items-center gap-2 transition ${
                isSubmit || fichier.length === 0
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-yellow-400 hover:bg-yellow-300 text-gray-900"
              }`}
            >
              {isSubmit ? (
                <>
                  <span className="loading loading-spinner loading-xs" />{" "}
                  Chargement…
                </>
              ) : (
                "Charger"
              )}
            </button>
          </div>
        </div>
      </dialog>

      {/* Add / Edit PBO Modal */}
      <dialog id="add_pbo" className="modal">
        <div className="modal-box rounded-2xl p-6 max-w-lg">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-bold text-gray-900">
              {selectItem ? "Modifier le PBO" : "Nouveau PBO"}
            </h2>
            <form method="dialog">
              <button id="close" className={closeBtnCls}>
                ✕
              </button>
            </form>
          </div>

          {error && <p className="text-xs text-red-500 mb-4">{error}</p>}

          <form onSubmit={handleSubmit(submit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Nom du PBO <span className="text-red-500">*</span>
                </label>
                <input
                  {...register("nom", { required: true })}
                  type="text"
                  defaultValue={selectItem?.nom}
                  placeholder="ex: PBO-001"
                  className={inputCls}
                />
                {errors.nom && (
                  <p className="text-xs text-red-500 mt-1">Obligatoire</p>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Nombre de coupleurs <span className="text-red-500">*</span>
                </label>
                <input
                  {...register("nombreCoupleur", { required: true })}
                  type="number"
                  min={0}
                  defaultValue={selectItem?.listCoupleurs?.length}
                  className={`${inputCls} [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                />
                {errors.nombreCoupleur && (
                  <p className="text-xs text-red-500 mt-1">Obligatoire</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Latitude <span className="text-red-500">*</span>
                </label>
                <input
                  {...register("latitude", { required: true })}
                  type="text"
                  value={latitude ?? ""}
                  onChange={(e) => setLatitude(e.target.value)}
                  placeholder="ex: 5.3544"
                  className={inputCls}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Longitude <span className="text-red-500">*</span>
                </label>
                <input
                  {...register("longitude", { required: true })}
                  type="text"
                  value={longitude ?? ""}
                  onChange={(e) => setLongitude(e.target.value)}
                  placeholder="ex: -3.9784"
                  className={inputCls}
                />
              </div>
            </div>

            <button
              type="button"
              onClick={handleClick}
              className="h-9 px-4 flex items-center gap-2 bg-gray-900 hover:bg-gray-700 text-white text-xs font-medium rounded-lg transition"
            >
              {loadingLocalisation ? (
                <ThreeDots
                  height="16"
                  width="32"
                  radius="9"
                  color="#fff"
                  visible={true}
                />
              ) : latitude && longitude ? (
                "Actualiser la position"
              ) : (
                "Utiliser ma position"
              )}
            </button>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Commune{" "}
                  {!selectItem && <span className="text-red-500">*</span>}
                </label>
                <input
                  {...register("commune", { required: !selectItem })}
                  type="text"
                  placeholder="ex: Cocody"
                  defaultValue={selectItem?.commune}
                  className={inputCls}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Quartier{" "}
                  {!selectItem && <span className="text-red-500">*</span>}
                </label>
                <input
                  {...register("quartier", { required: !selectItem })}
                  type="text"
                  placeholder="ex: Riviera"
                  defaultValue={selectItem?.quartier}
                  className={inputCls}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isSubmit}
              className={`w-full h-10 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition ${
                isSubmit
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-yellow-400 hover:bg-yellow-300 text-gray-900"
              }`}
            >
              {isSubmit ? (
                <>
                  <span className="loading loading-spinner loading-xs" />{" "}
                  Enregistrement…
                </>
              ) : selectItem ? (
                "Enregistrer les modifications"
              ) : (
                "Enregistrer"
              )}
            </button>
          </form>
        </div>
      </dialog>

      {/* Delete Modal */}
      <dialog id="delete" className="modal">
        <div className="modal-box rounded-2xl p-6 max-w-md">
          <p className="text-base font-bold text-gray-900 mb-1">
            Confirmer la suppression
          </p>
          <p className="text-sm text-gray-500 mb-6">
            Le PBO{" "}
            <span className="font-semibold text-gray-800">
              {selectItem?.nom}
            </span>{" "}
            sera définitivement supprimé.
          </p>
          <div className="flex justify-end gap-2">
            <form method="dialog">
              <button className="h-9 px-4 rounded-lg text-sm font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 transition">
                Annuler
              </button>
            </form>
            <form method="dialog">
              <button
                onClick={() => {
                  deleteOne(DELETE_PBO, selectItem.id)
                    .then(() => {
                      toast.success("PBO supprimé");
                      listPbo.getData("page=0&param=&size=30");
                    })
                    .catch(() => toast.error("Erreur lors de la suppression"));
                }}
                className="h-9 px-4 rounded-lg text-sm font-medium bg-red-500 hover:bg-red-600 text-white transition"
              >
                Supprimer
              </button>
            </form>
          </div>
        </div>
      </dialog>

      {/* Grid */}
      {checkTagValidity("LISTE_PBO") && (
        <>
          {listPbo?.loading ? (
            <Loader />
          ) : listPbo?.data?.data?.length === 0 ? (
            <div className="flex items-center justify-center h-48">
              <p className="text-sm text-gray-400">Aucun PBO trouvé</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {listPbo?.data?.data?.map((item) => (
                <div
                  key={item.id}
                  className="relative bg-white border border-gray-200 rounded-xl p-3 flex items-center gap-3 hover:shadow-md hover:border-yellow-300 transition-all duration-200"
                >
                  {/* Menu */}
                  <div className="dropdown dropdown-end absolute top-2 right-2">
                    <div
                      tabIndex={0}
                      role="button"
                      className="w-7 h-7 flex items-center justify-center rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-500 transition cursor-pointer"
                    >
                      <MoreHorizontal size={15} />
                    </div>
                    <ul
                      tabIndex={0}
                      className="dropdown-content z-10 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg w-44 p-1.5"
                    >
                      <li>
                        <button
                          className="w-full h-8 px-3 text-left text-xs font-medium text-gray-700 rounded-lg hover:bg-gray-50 transition"
                          onClick={() => {
                            setSelectItem(item);
                            document.getElementById("add_pbo").showModal();
                          }}
                        >
                          Modifier
                        </button>
                      </li>
                      <li>
                        <Link
                          to={`${item.id}/Liste-port/${encodeURIComponent(item.nom)}`}
                          className="block h-8 px-3 leading-8 text-xs font-medium text-gray-700 rounded-lg hover:bg-gray-50 transition"
                        >
                          Voir les brins
                        </Link>
                      </li>
                      {checkTagValidity("SUPPRIMER_PBO") && (
                        <li>
                          <button
                            className="w-full h-8 px-3 text-left text-xs font-medium text-red-600 rounded-lg hover:bg-red-50 transition"
                            onClick={() => {
                              setSelectItem(item);
                              document.getElementById("delete").showModal();
                            }}
                          >
                            Supprimer
                          </button>
                        </li>
                      )}
                    </ul>
                  </div>

                  {/* Icon */}
                  <div className="w-10 h-10 rounded-xl bg-amber-50 border border-amber-100 flex items-center justify-center shrink-0">
                    <ConnectIcon />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0 pr-6">
                    <p className="text-xs font-semibold text-gray-900 truncate">
                      {item.nom}
                    </p>
                    {(item.commune || item.quartier) && (
                      <p className="text-xs text-gray-400 mt-0.5 truncate">
                        {[item.commune, item.quartier]
                          .filter(Boolean)
                          .join(", ")}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Pagination */}
      <div className="flex justify-end mt-6">
        <Pagination
          onChange={(_, v) => more(v)}
          count={listPbo.count}
          variant="outlined"
          color="primary"
          shape="rounded"
          size="small"
        />
      </div>
    </div>
  );
};
